﻿import { h, Fragment, FunctionComponent } from 'preact';
import { memo } from 'preact/compat';
import { KpiVisualiser } from './KPIVisualiser';
import styles from './KPIVisualiser.module.scss';
import KpiVisualiserContext, { useLocalized } from './KPIVisualiser_Context';
import { dateToXPos, DAYS_TO_MS, extractTimeFromDate, GRID_MARGIN_TOP, localDateToUtcDate, MS_TO_DAYS } from './Utils';

interface Props {
    startDate: Date;
    endDate: Date;
    zoom: number;
    scrollX: number;
}

export const KpiVisualiser_Grid_Lines: FunctionComponent<Props> = memo(({ startDate, endDate, zoom, scrollX }) => {
    const startDay = Math.floor(startDate.getTime() * MS_TO_DAYS);
    const endDay = Math.ceil(endDate.getTime() * MS_TO_DAYS);
    const elements: preact.JSX.Element[] = [];

    const visualiserHeight = KpiVisualiser.instance?.props.domElement.clientHeight ?? 2000.0;
    const visualiserWidth = KpiVisualiser.instance?.props.domElement.clientWidth ?? 2000.0;
    const leftClipEdge = -scrollX;
    const rightClipEdge = leftClipEdge + visualiserWidth;

    for (let i = startDay; i < endDay; ++i) {
        const d = localDateToUtcDate(new Date(i * DAYS_TO_MS));
        const x = dateToXPos(d, zoom);
        const d2 = localDateToUtcDate(new Date((i + 1) * DAYS_TO_MS));
        const x2 = dateToXPos(d2, zoom);

        if (x2 < leftClipEdge) {
            // Line is out of range. Skip.
            continue;
        }

        if (x > rightClipEdge) {
            // Line and future lines will be out of range. Skip rest.
            break;
        }

        let workingHours = KpiVisualiserContext.GetWorkingHoursForDate(d);
        if (workingHours) {
            const startTime = new Date(workingHours.localStartTime);
            const endTime = new Date(workingHours.localEndTime);
            const start = new Date(d.getTime() + extractTimeFromDate(startTime));
            const end = new Date(d.getTime() + extractTimeFromDate(endTime));

            const startX = dateToXPos(start, zoom);
            const endX = dateToXPos(end, zoom);

            elements.push((
                <rect key={`${d.getTime()}-wh`} x={startX} y={GRID_MARGIN_TOP} width={endX - startX} height={visualiserHeight} class={styles.gridWorkingTime} />
            ));
        }

        elements.push((
            <line key={`${d.getTime()}-l`} x1={x} y1={GRID_MARGIN_TOP} x2={x} y2={visualiserHeight} class={styles.gridLine} />
        ));

        if (zoom >= 1.2) { // Show hour lines
            const showMinorLines = zoom >= 2.0;

            for (let j = 1; j < 24; ++j) {
                const hx = x + (x2 - x) * (j / 24.0);

                if (!showMinorLines && j % 6 !== 0) {
                    continue;
                }

                elements.push((
                    <line key={`${d.getTime()}-l-${j}-h`} x1={hx} y1={GRID_MARGIN_TOP} x2={hx} y2={visualiserHeight} class={j % 6 === 0 ? styles.gridHourLineMajor : styles.gridHourLineMinor} />
                ));
            }
        }
    }

    return <>{elements}</>;
});


export const KpiVisualiser_Grid_Top: FunctionComponent<Props> = memo(({ startDate, endDate, zoom, scrollX }) => {
    const nowX = dateToXPos(new Date(), zoom);
    const startDay = Math.floor(startDate.getTime() * MS_TO_DAYS);
    const endDay = Math.ceil(endDate.getTime() * MS_TO_DAYS);
    const elements: preact.JSX.Element[] = [];

    const visualiserWidth = KpiVisualiser.instance?.props.domElement.clientWidth ?? 2000.0;
    const leftClipEdge = -scrollX - 32.0;
    const rightClipEdge = leftClipEdge + visualiserWidth + 64.0;

    for (let i = startDay; i < endDay; ++i) {
        const d = new Date(i * DAYS_TO_MS);
        const x = dateToXPos(localDateToUtcDate(d), zoom);

        if (x < leftClipEdge) {
            // Text is out of range. Skip.
            continue;
        }

        if (x > rightClipEdge) {
            // Text and future text will be out of range. Skip rest.
            break;
        }

        let y = GRID_MARGIN_TOP - 32.0;
        if (zoom < 0.7) {
            y += 8 * (i % 2 === 0 ? 1 : -1) + 6;
        }

        const fontSize = zoom < 0.4 ? "10pt" : "1em";

        elements.push((
            <text key={`${i}-t`} x={x} y={y} class={styles.gridDateLabel} font-size={fontSize}>
                {d.toLocaleDateString()}
            </text>
        ));
    }

    return (
        <>
            {elements}
            <text x={nowX} y={GRID_MARGIN_TOP - 52.0} fill="rgba(0,196,0,0.8)" text-anchor="middle" dominant-baseline="hanging">
                {useLocalized("lbl:now", "Now")}
            </text>
        </>
    );
});